import React, { useState, useMemo } from 'react';
import { X, Search, Star, Plus } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import type { Agency } from '../types';

const PHONE_TYPE_OPTIONS = [
  { id: 2, label: 'Work Landline' },
  { id: 1, label: 'Work Mobile' },
  { id: 4, label: 'Personal Mobile' },
  { id: 3, label: 'Home Mobile' },
  { id: 5, label: 'Duty Phone' },
  { id: 6, label: 'Ops Center' },
  { id: 7, label: 'Fax' },
  { id: 8, label: 'Other' },
];

interface PhoneRow {
  phone_type_id: number;
  phone_number: string;
  extension: string;
  is_preferred: boolean;
  sort_order: number;
}

interface Props {
  onClose: () => void;
  defaultAgencyId?: string;
}

const AddContactModal: React.FC<Props> = ({ onClose, defaultAgencyId }) => {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [agencySearch, setAgencySearch] = useState('');
  const [roleSearch, setRoleSearch] = useState('');
  const [selectedRoles, setSelectedRoles] = useState<Set<string>>(new Set());
  const [selectedLists, setSelectedLists] = useState<Set<string>>(new Set());

  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    agency_id: defaultAgencyId ?? '',
    title: '',
    email_primary: '',
    classification_id: 1,
    notes: '',
  });

  const [phones, setPhones] = useState<PhoneRow[]>([
    { phone_type_id: 2, phone_number: '', extension: '', is_preferred: true, sort_order: 0 },
  ]);

  const addPhone = () => {
    setPhones((prev) => [
      ...prev,
      { phone_type_id: 1, phone_number: '', extension: '', is_preferred: prev.length === 0, sort_order: prev.length },
    ]);
  };

  const removePhone = (idx: number) => {
    setPhones((prev) => {
      const next = prev.filter((_, i) => i !== idx).map((p, i) => ({ ...p, sort_order: i }));
      if (next.length > 0 && !next.some((p) => p.is_preferred)) next[0].is_preferred = true;
      return next;
    });
  };

  const setPreferred = (idx: number) => {
    setPhones((prev) => prev.map((p, i) => ({ ...p, is_preferred: i === idx })));
  };

  const updatePhone = (idx: number, field: keyof PhoneRow, value: string | number | boolean) => {
    setPhones((prev) => prev.map((p, i) => i === idx ? { ...p, [field]: value } : p));
  };

  const { data: agencies = [] } = useQuery<Agency[]>({
    queryKey: ['agencies-all'],
    queryFn: () => api.getAgencies({ limit: 500 }).then((r) => r.data ?? []),
    staleTime: 5 * 60 * 1000,
  });

  const { data: allRoles = [] } = useQuery<any[]>({
    queryKey: ['roles-all'],
    queryFn: () => api.getRoles(),
    staleTime: 5 * 60 * 1000,
  });

  const { data: listsData } = useQuery<any>({
    queryKey: ['lists'],
    queryFn: () => api.getLists(),
    staleTime: 60 * 1000,
  });
  const allLists: any[] = listsData?.data ?? [];

  const filteredAgencies = useMemo(() =>
    agencySearch.trim()
      ? agencies.filter((a) =>
          a.name.toLowerCase().includes(agencySearch.toLowerCase()) ||
          (a.acronym ?? '').toLowerCase().includes(agencySearch.toLowerCase())
        )
      : agencies,
    [agencies, agencySearch]
  );

  const filteredRoles = useMemo(() =>
    allRoles.filter((r) => r.name.toLowerCase().includes(roleSearch.toLowerCase())),
    [allRoles, roleSearch]
  );

  const selectedAgency = agencies.find((a) => a.id === form.agency_id);

  const toggleRole = (id: string) => {
    setSelectedRoles((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleList = (id: string) => {
    setSelectedLists((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleSubmit = async () => {
    if (!form.first_name.trim() || !form.last_name.trim() || !form.agency_id) return;
    setSaving(true);
    setError('');
    try {
      const cleanPhones = phones
        .filter((p) => p.phone_number.trim())
        .map((p, i) => ({ ...p, phone_number: p.phone_number.trim(), sort_order: i }));
      const res = await api.createContact({
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        agency_id: form.agency_id,
        title: form.title.trim() || null,
        email_primary: form.email_primary.trim() || null,
        classification_id: form.classification_id,
        phones: cleanPhones,
        roles: Array.from(selectedRoles),
        list_ids: Array.from(selectedLists),
      });
      const newId = res.data?.id ?? res.id;
      qc.invalidateQueries({ queryKey: ['contacts'] });
      qc.invalidateQueries({ queryKey: ['lists'] });
      onClose();
      if (newId) navigate(`/contacts/${newId}`);
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Failed to create contact.');
    } finally {
      setSaving(false);
    }
  };

  const canSubmit = form.first_name.trim() && form.last_name.trim() && form.agency_id && !saving;

  return (
    <div className="modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div
        className="modal"
        onMouseDown={(e) => e.stopPropagation()}
        style={{ maxWidth: 560, width: '100%', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexShrink: 0 }}>
          <h3 style={{ margin: 0 }}>Add Contact</h3>
          <button className="btn btn-ghost" onClick={onClose}><X size={16} /></button>
        </div>

        <div style={{ overflowY: 'auto', flex: 1, paddingRight: 2 }}>
          {/* Name row */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div>
              <label className="field-label">First Name *</label>
              <input
                className="field-input"
                type="text"
                autoFocus
                value={form.first_name}
                onChange={(e) => setForm({ ...form, first_name: e.target.value })}
              />
            </div>
            <div>
              <label className="field-label">Last Name *</label>
              <input
                className="field-input"
                type="text"
                value={form.last_name}
                onChange={(e) => setForm({ ...form, last_name: e.target.value })}
              />
            </div>
          </div>

          {/* Agency */}
          <div style={{ marginBottom: 12 }}>
            <label className="field-label">Agency *</label>
            {selectedAgency ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ flex: 1, padding: '7px 10px', background: 'var(--gray-50)', border: '1px solid var(--gray-300)', borderRadius: 6, fontSize: 14 }}>
                  {selectedAgency.acronym} — {selectedAgency.name}
                </div>
                {!defaultAgencyId && (
                  <button className="btn btn-ghost" style={{ padding: '6px 8px', fontSize: 12 }} onClick={() => setForm({ ...form, agency_id: '' })}>
                    Change
                  </button>
                )}
              </div>
            ) : (
              <>
                <div style={{ position: 'relative', marginBottom: 6 }}>
                  <Search size={13} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--gray-500)' }} />
                  <input
                    className="field-input"
                    style={{ paddingLeft: 30 }}
                    placeholder="Search agencies…"
                    value={agencySearch}
                    onChange={(e) => setAgencySearch(e.target.value)}
                  />
                </div>
                <div style={{ maxHeight: 160, overflowY: 'auto', border: '1px solid var(--gray-200)', borderRadius: 6 }}>
                  {filteredAgencies.slice(0, 40).map((a) => (
                    <div
                      key={a.id}
                      onClick={() => { setForm({ ...form, agency_id: a.id }); setAgencySearch(''); }}
                      style={{ padding: '8px 12px', borderBottom: '1px solid var(--gray-100)', cursor: 'pointer', fontSize: 14 }}
                    >
                      <strong>{a.acronym}</strong> — {a.name}
                    </div>
                  ))}
                  {filteredAgencies.length === 0 && (
                    <div style={{ padding: 12, fontSize: 14, color: 'var(--gray-500)', textAlign: 'center' }}>No agencies found</div>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Title */}
          <div style={{ marginBottom: 12 }}>
            <label className="field-label">Title</label>
            <input
              className="field-input"
              type="text"
              placeholder="e.g. Emergency Management Director"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
            />
          </div>

          {/* Phones */}
          <div style={{ marginBottom: 12 }}>
            <label className="field-label">Phone Numbers</label>
            {phones.map((phone, idx) => (
              <div key={idx} style={{ display: 'flex', gap: 6, marginBottom: 6, alignItems: 'center' }}>
                <select
                  value={phone.phone_type_id}
                  onChange={(e) => updatePhone(idx, 'phone_type_id', Number(e.target.value))}
                  style={{ padding: '6px 8px', border: '1px solid var(--gray-300)', borderRadius: 6, fontSize: 12, flexShrink: 0 }}
                >
                  {PHONE_TYPE_OPTIONS.map((t) => (
                    <option key={t.id} value={t.id}>{t.label}</option>
                  ))}
                </select>
                <input
                  type="tel"
                  className="field-input"
                  style={{ flex: 1 }}
                  placeholder="212-555-0100"
                  value={phone.phone_number}
                  onChange={(e) => updatePhone(idx, 'phone_number', e.target.value)}
                />
                <input
                  type="text"
                  className="field-input"
                  style={{ width: 64, flexShrink: 0 }}
                  placeholder="Ext."
                  value={phone.extension}
                  onChange={(e) => updatePhone(idx, 'extension', e.target.value)}
                />
                <button
                  title={phone.is_preferred ? 'Primary' : 'Set as primary'}
                  onClick={() => setPreferred(idx)}
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer', padding: '4px',
                    color: phone.is_preferred ? 'var(--amber-text)' : 'var(--gray-300)', flexShrink: 0,
                  }}
                >
                  <Star size={15} fill={phone.is_preferred ? '#f59e0b' : 'none'} />
                </button>
                <button
                  onClick={() => removePhone(idx)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray-500)', padding: '4px', flexShrink: 0 }}
                >
                  <X size={14} />
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={addPhone}
              style={{ background: 'none', border: 'none', color: 'var(--blue)', fontSize: 12, cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', gap: 4 }}
            >
              <Plus size={13} /> Add Phone
            </button>
          </div>

          {/* Email + Classification */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 12, marginBottom: 12 }}>
            <div>
              <label className="field-label">Email</label>
              <input
                className="field-input"
                type="email"
                value={form.email_primary}
                onChange={(e) => setForm({ ...form, email_primary: e.target.value })}
              />
            </div>
            <div>
              <label className="field-label">Classification</label>
              <select
                className="field-input"
                value={form.classification_id}
                onChange={(e) => setForm({ ...form, classification_id: Number(e.target.value) })}
              >
                <option value={1}>Standard</option>
                <option value={2}>Restricted</option>
              </select>
            </div>
          </div>

          {/* Notes */}
          <div style={{ marginBottom: 16 }}>
            <label className="field-label">Notes</label>
            <textarea
              className="field-input"
              rows={2}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Optional notes…"
              maxLength={1000}
            />
          </div>

          {/* Roles */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
              Assign Roles
            </div>
            <div style={{ fontSize: 12, color: 'var(--gray-500)', marginBottom: 8 }}>
              Optional — roles can be assigned later
            </div>
            <div style={{ position: 'relative', marginBottom: 6 }}>
              <Search size={13} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--gray-500)' }} />
              <input
                className="field-input"
                style={{ paddingLeft: 30, fontSize: 12 }}
                placeholder="Filter roles…"
                value={roleSearch}
                onChange={(e) => setRoleSearch(e.target.value)}
              />
            </div>
            <div style={{ maxHeight: 140, overflowY: 'auto', border: '1px solid var(--gray-200)', borderRadius: 6 }}>
              {filteredRoles.map((r: any) => (
                <label
                  key={r.id}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 12px', borderBottom: '1px solid var(--gray-100)', cursor: 'pointer', fontSize: 14 }}
                >
                  <input
                    type="checkbox"
                    checked={selectedRoles.has(r.id)}
                    onChange={() => toggleRole(r.id)}
                    style={{ width: 14, height: 14 }}
                  />
                  {r.name}
                </label>
              ))}
              {filteredRoles.length === 0 && (
                <p style={{ color: 'var(--gray-500)', padding: 10, margin: 0, fontSize: 14 }}>No roles match.</p>
              )}
            </div>
            {selectedRoles.size > 0 && (
              <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 4 }}>{selectedRoles.size} role{selectedRoles.size !== 1 ? 's' : ''} selected</div>
            )}
          </div>

          {/* Lists */}
          {allLists.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
                Add to Lists
              </div>
              <div style={{ fontSize: 12, color: 'var(--gray-500)', marginBottom: 8 }}>
                Optional — can be added later
              </div>
              <div style={{ maxHeight: 120, overflowY: 'auto', border: '1px solid var(--gray-200)', borderRadius: 6 }}>
                {allLists.map((l: any) => (
                  <label
                    key={l.id}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 12px', borderBottom: '1px solid var(--gray-100)', cursor: 'pointer', fontSize: 14 }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedLists.has(l.id)}
                      onChange={() => toggleList(l.id)}
                      style={{ width: 14, height: 14 }}
                    />
                    {l.name}
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>

        {error && <p style={{ color: 'var(--red)', fontSize: 12, margin: '8px 0 0', flexShrink: 0 }}>{error}</p>}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16, flexShrink: 0 }}>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button
            className="btn btn-primary"
            disabled={!canSubmit}
            onClick={handleSubmit}
          >
            {saving ? 'Creating…' : 'Create Contact'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddContactModal;
